<script setup>
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { formatPercent } from '../../utils/format_percent';

  const { t } = useI18n();
  const i18nScope = 'components.sales_dashboard';

  const props = defineProps({
    // [{ key: 'active', label: 'Active', count: 12, colour: '#198754' }], in
    // charting order. The parent picks the colours: it knows whether its
    // categories have a fixed hue or take one by rank.
    entries: { type: Array, default: () => [] },
    // What the total in the centre counts, e.g. "loans"
    totalLabel: { type: String, required: true }
  });

  const SIZE = 240;
  const CENTRE = SIZE / 2;
  const OUTER = 96;
  const INNER = 60;

  const hovered = ref(null);

  const total = computed(() => props.entries.reduce((sum, entry) => sum + entry.count, 0));

  // Zero-count entries are dropped from the ring but kept in the table, so
  // the chart stays legible without hiding the fact that they are zero.
  const slices = computed(() => {
    if (total.value === 0) return [];

    let angle = -Math.PI / 2;
    return props.entries
      .filter((entry) => entry.count > 0)
      .map((entry) => {
        const share = entry.count / total.value;
        const start = angle;
        const end = angle + share * Math.PI * 2;
        angle = end;

        return {
          ...entry,
          share,
          midAngle: start + (end - start) / 2,
          path: arcPath(start, end)
        };
      });
  });

  function pointOn(radius, angle) {
    return [CENTRE + radius * Math.cos(angle), CENTRE + radius * Math.sin(angle)];
  }

  function arcPath(start, end) {
    // A slice covering the whole ring cannot be drawn as a single arc: its
    // start and end points coincide, so split it in half.
    if (end - start >= Math.PI * 2 - 0.0001) {
      const mid = start + Math.PI;
      return `${arcPath(start, mid)} ${arcPath(mid, end - 0.0001)}`;
    }

    const [outerStartX, outerStartY] = pointOn(OUTER, start);
    const [outerEndX, outerEndY] = pointOn(OUTER, end);
    const [innerEndX, innerEndY] = pointOn(INNER, end);
    const [innerStartX, innerStartY] = pointOn(INNER, start);
    const largeArc = end - start > Math.PI ? 1 : 0;

    return [
      `M${outerStartX},${outerStartY}`,
      `A${OUTER},${OUTER} 0 ${largeArc} 1 ${outerEndX},${outerEndY}`,
      `L${innerEndX},${innerEndY}`,
      `A${INNER},${INNER} 0 ${largeArc} 0 ${innerStartX},${innerStartY}`,
      'Z'
    ].join(' ');
  }

  function formatCount(count) {
    return new Intl.NumberFormat().format(count);
  }

  function sliceFigures(slice) {
    return `${formatCount(slice.count)} (${formatPercent(slice.share)})`;
  }

  function sliceLabel(slice) {
    return `${slice.label}: ${sliceFigures(slice)}`;
  }

  // Same estimate-the-width approach as TimeSeriesChart: SVG will not size a
  // box to its text, and these labels are short.
  const TOOLTIP_HEIGHT = 24;
  const TOOLTIP_CHAR_WIDTH = 6.9;
  const TOOLTIP_PADDING_X = 9;
  const RING_GAP = 8;

  const tooltip = computed(() => {
    const slice = hoveredSlice.value;
    if (!slice) return null;

    // A label too long to fit the drawing gives way to the figures alone: the
    // centre and the legend still name the slice.
    let text = sliceLabel(slice);
    if (textWidth(text) > SIZE - 4) text = sliceFigures(slice);
    const width = textWidth(text);
    const [anchorX, anchorY] = pointOn(OUTER + RING_GAP, slice.midAngle);

    return {
      text: text,
      width: width,
      height: TOOLTIP_HEIGHT,
      // Clamped into the viewBox: a slice near an edge would otherwise push
      // its box outside the drawing and clip.
      x: clamp(anchorX - width / 2, 2, SIZE - width - 2),
      y: clamp(anchorY - TOOLTIP_HEIGHT / 2, 2, SIZE - TOOLTIP_HEIGHT - 2)
    };
  });

  function textWidth(text) {
    return text.length * TOOLTIP_CHAR_WIDTH + TOOLTIP_PADDING_X * 2;
  }

  const hoveredSlice = computed(() => slices.value.find((entry) => entry.key === hovered.value) || null);

  // Roughly what fits across the hole at the centre label's size. A longer
  // label is shown as its share instead; the legend beside carries the name.
  const CENTRE_LABEL_MAX_CHARS = 18;

  const centreLabel = computed(() => {
    const slice = hoveredSlice.value;
    if (!slice) return props.totalLabel;

    return slice.label.length <= CENTRE_LABEL_MAX_CHARS ? slice.label : formatPercent(slice.share);
  });

  function clamp(value, lowest, highest) {
    return Math.min(Math.max(value, lowest), Math.max(lowest, highest));
  }
</script>

<template>
  <div class="donut-chart">
    <div v-if="total === 0" class="text-center text-muted py-4">
      {{ t(`${i18nScope}.no_data`) }}
    </div>

    <div v-else class="donut-layout">
      <svg
        :viewBox="`0 0 ${SIZE} ${SIZE}`"
        class="donut-svg"
        preserveAspectRatio="xMidYMid meet"
        role="presentation"
        focusable="false"
      >
        <path
          v-for="slice in slices"
          :key="slice.key"
          class="slice"
          :class="{ 'is-dimmed': hovered !== null && hovered !== slice.key }"
          :d="slice.path"
          :fill="slice.colour"
          tabindex="0"
          role="img"
          :aria-label="sliceLabel(slice)"
          @mouseenter="hovered = slice.key"
          @mouseleave="hovered = null"
          @focus="hovered = slice.key"
          @blur="hovered = null"
        />

        <!-- Centre readout: the hovered slice, or the total when nothing is -->
        <text class="centre-value" :x="CENTRE" :y="CENTRE - 2" text-anchor="middle">
          {{ formatCount(hoveredSlice ? hoveredSlice.count : total) }}
        </text>
        <text class="centre-label" :x="CENTRE" :y="CENTRE + 16" text-anchor="middle">
          {{ centreLabel }}
        </text>

        <!-- Not called "tooltip": Bootstrap's global .tooltip rule sets
             opacity 0, which would render this invisible. -->
        <g v-if="tooltip" class="chart-tooltip" aria-hidden="true">
          <rect
            class="chart-tooltip-box"
            :x="tooltip.x"
            :y="tooltip.y"
            :width="tooltip.width"
            :height="tooltip.height"
            rx="4"
          />
          <text
            class="chart-tooltip-text"
            :x="tooltip.x + tooltip.width / 2"
            :y="tooltip.y + tooltip.height / 2"
            text-anchor="middle"
            dominant-baseline="central"
          >{{ tooltip.text }}</text>
        </g>
      </svg>

      <ul class="donut-legend list-unstyled mb-0">
        <li
          v-for="slice in slices"
          :key="`legend-${slice.key}`"
          class="legend-item"
          :class="{ 'is-dimmed': hovered !== null && hovered !== slice.key }"
          @mouseenter="hovered = slice.key"
          @mouseleave="hovered = null"
        >
          <span class="legend-swatch" :style="{ backgroundColor: slice.colour }" aria-hidden="true"></span>
          <span class="legend-label">{{ slice.label }}</span>
          <span class="legend-value">
            {{ formatCount(slice.count) }}
            <span class="text-muted">({{ formatPercent(slice.share) }})</span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
  .donut-layout {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1.5rem;
  }

  .donut-svg {
    flex: 0 0 200px;
    width: 200px;
    max-width: 100%;
    height: auto;
  }

  .donut-legend {
    flex: 1 1 200px;
    min-width: 200px;
    font-size: 0.875rem;
  }

  .slice {
    cursor: default;
    stroke: #fff;
    stroke-width: 2;
  }

  .slice.is-dimmed,
  .legend-item.is-dimmed {
    opacity: 0.4;
  }

  .slice:focus-visible {
    outline: 2px solid #212529;
  }

  .centre-value {
    fill: #212529;
    font-size: 22px;
    font-weight: 600;
  }

  .centre-label {
    fill: #6c757d;
    font-size: 11px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.2rem 0;
  }

  .legend-swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex: 0 0 auto;
  }

  .legend-label {
    flex: 1 1 auto;
  }

  .legend-value {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
  }

  .chart-tooltip {
    pointer-events: none;
  }

  .chart-tooltip-box {
    fill: #212529;
    fill-opacity: 0.92;
  }

  .chart-tooltip-text {
    fill: #fff;
    font-size: 12px;
    font-weight: 600;
  }
</style>
